<template>
	<BuyersOrderRow :hideExtraColumns="hideProfits">
		<template #left-icon>
			<span class="buyers-order-upfront-capped-icon">
				<Pill
					v-if="showAdjustmentAfterDealFinalized"
					label="A"
					status="info"
					:size="10"
					:includeDot="false"
				/>
				<Pill
					v-else-if="showPrice && bo.isLease()"
					:title="currentBucketItem.isFront ? 'Upfront' : 'Capped'"
					:label="currentBucketItem.isFront ? 'U' : 'C'"
					:status="currentBucketItem.isFront ? 'success' : 'warning'"
					:size="10"
					:includeDot="false"
				/>
			</span>
		</template>

		<template #info>
			<component
				:is="isClickable ? 'button' : 'div'"
				@click="onItemClick()"
				:class="{
					'bucket-item-name-container': true,
					'button-link': isClickable,
					'ignore-all-locks': true,
				}"
			>
				<div class="bucket-item-name">
					<span>{{ currentBucketItem.name }}</span>

					<div
						v-if="currentBucketItem.metadata && currentBucketItem.metadata.subName"
						class="bucket-item-subname"
					>
						{{ currentBucketItem.metadata.subName }}
					</div>
				</div>
				<span v-if="currentBucketItem.metadata && currentBucketItem.metadata.preloadAmount">
					Preloaded:
					{{ $filters.currency2(currentBucketItem.metadata.preloadAmount) }}
				</span>
				<div class="rollup-container">
					<Pill
						v-if="showRollupTitle"
						:label="currentBucketItem.metadata?.rollupTitle"
						status="info"
						:size="10"
						:includeDot="false"
					/>
					<Pill
						v-if="bo.isBucketItemTaxed(currentBucketItem)"
						label="Taxed"
						status="warning"
						:size="10"
						:includeDot="false"
					/>
				</div>
			</component>
		</template>

		<template #right-icon>
			<div
				v-if="!bucketContent.bucketItem.isValid(bucketType, fimenuValidation)"
				class="error-text"
			>
				<i class="fas fa-exclamation-circle"></i>
			</div>
			<span v-else-if="currentBucketItem.sign == 1">+</span>
			<span v-else-if="currentBucketItem.sign == -1">-</span>
		</template>

		<template #column-1>
			<InputCurrency
				v-if="showPrice"
				v-model:saturn="currentBucketItem.amount"
				:modelValue="currentBucketItem.amount"
				:disabled="!currentBucketItem.allowChangeAmount"
				:precision="2"
				iconSide="left"
				@change="updateSourceAmount()"
				:negative="true"
				:invalid="!currentBucketItem.isAmountValid()"
			>
				<template #validation>
					<div v-if="!currentBucketItem.isAmountValid()">
						{{ itemValidationMessage ?? 'Allowance is Required' }}
					</div>
				</template>
			</InputCurrency>
		</template>

		<template #column-2>
			<InputCurrency
				v-if="currentBucketItem.cost != null || currentBucketItem.showCostAndProfitEvenIfNull"
				v-model:saturn="currentBucketItem.cost"
				:disabled="isClickable || currentBucketItem.allowChangeCost == false || costLockCondition()"
				:precision="2"
				iconSide="left"
				class="ignore-deal-lock"
				@change="updateSourceCost()"
				:negative="true"
				:invalid="!currentBucketItem.isCostValid()"
				@blur="() => onBlurCost(!currentBucketItem.isCostValid())"
			>
				<template #validation>
					<div v-if="!currentBucketItem.isCostValid()">
						{{ itemValidationMessage ?? 'Actual Cash Value is Required' }}
					</div>
				</template>
			</InputCurrency>
		</template>

		<template #column-3>
			<InputCurrency
				v-if="currentBucketItem.profit != null || currentBucketItem.showCostAndProfitEvenIfNull"
				v-model:saturn="currentBucketItem.profit"
				:disabled="true"
				:precision="2"
				iconSide="left"
				:negative="true"
			>
			</InputCurrency>
		</template>
	</BuyersOrderRow>
</template>

<script lang="ts">
	export interface IBuyersOrderBucketItemProps {
		bo: FIMenuBuyersOrder;
		fimenu: FIMenu;
		bucketContent: IBucketContent;
		costLockCondition?: () => boolean;
		itemValidationMessage?: string;
		fimenuValidation?: any;
		hideProfits?: boolean;
		hideSubTotal?: boolean;
		sourceData?: any[];
	}
</script>

<script setup lang="ts">
	import BuyersOrderRow from '@/components/buyers-order/BuyersOrderRow.vue';
	import { computed } from 'vue';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucketItem from '@core/classes/FIMenuBuyersOrderBucketItem';
	import { IBucketContent } from '@core/_interfaces/SharedInterfaces';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import Pill from '@core/components/Pill.vue';

	const {
		bo,
		fimenu,
		bucketContent,
		costLockCondition = () => false,
		itemValidationMessage,
		fimenuValidation,
		hideProfits = false,
		hideSubTotal = false,
		sourceData,
	} = defineProps<IBuyersOrderBucketItemProps>();

	const emit = defineEmits<{
		blur: [isInvalid: boolean];
		change: [];
		itemClick: [item: Partial<FIMenuBuyersOrderBucketItem>];
	}>();

	const currentBucketItem = computed(() => bucketContent.bucketItem);
	const bucketType = computed(() => bucketContent.parentBucket.bucketType);
	const showPrice = computed(() => bucketType.value !== 'Adjustments');

	const showRollupTitle = computed(() => bucketType.value.toLowerCase() === 'fees' && currentBucketItem.value.metadata?.rollupTitle);
	const showAdjustmentAfterDealFinalized = computed(() => {
		const adjustment = bo.additions.find(a => a.name === currentBucketItem.value.name && a.isAdjustment);
		if (!adjustment) return false;

		// Check if adjustment was created after deal status was updated to Finalized
		return fimenu.isDealFinalized() && fimenu.dealStatusUpdated && adjustment.createdAt.valueOf() > fimenu.dealStatusUpdated.valueOf();
	});

	const isClickable = computed(() => {
		return ['additions', 'adjustments', 'fees', 'rebates', 'cash down'].includes(bucketType.value.toLowerCase());
	});

	function onItemClick() {
		if (isClickable.value) {
			emit('itemClick', bucketContent.bucketItem);
		}
	}

	function updateSourceAmount() {
		if (sourceData) {
			bucketContent.bucketItem.updateSourceAmount(sourceData, bucketType.value, bo);
			emit('change');
		}
	}

	function updateSourceCost() {
		if (sourceData) {
			bucketContent.bucketItem.updateSourceCost(sourceData, bucketType.value);
			emit('change');
		}
	}

	function onBlurCost(isCostInvalid: boolean) {
		emit('blur', isCostInvalid);
	}
</script>

<style scoped>
	.rollup-container {
		display: flex;
		justify-content: center;
		gap: 5px;
	}

	.rollup-container .pill-container {
		justify-content: center !important;
		width: 50px;
	}
</style>
